import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Building2, Briefcase, Clock, CheckCircle, AlertCircle, FileText, MessageSquare, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const jobStatusColors = {
  assigned: 'bg-blue-100 text-blue-800',
  in_progress: 'bg-purple-100 text-purple-800',
  completed: 'bg-green-100 text-green-800',
  cancelled: 'bg-red-100 text-red-800',
};

const jobStatuses = ['assigned', 'in_progress', 'completed'];

export default function PartnerPortalPage() {
  const navigate = useNavigate();
  const { user, api } = useAuth();
  const [profile, setProfile] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);
  const [updateData, setUpdateData] = useState({ status: '', notes: '' });
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [showChat, setShowChat] = useState(false);
  const chatEndRef = useRef(null);

  useEffect(() => {
    if (!user) { navigate('/login'); return; }

    const fetchData = async () => {
      try {
        const profileRes = await api.get('/api/partner/profile');
        setProfile(profileRes.data);
        const jobsRes = await api.get('/api/partner/jobs');
        setJobs(jobsRes.data);
      } catch (err) {
        setError(err?.response?.data?.detail || 'You do not have access to the partner portal. Your application must be approved first.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [user, api, navigate]);

  const handleUpdateJob = async () => {
    try {
      const body = {};
      if (updateData.status) body.status = updateData.status;
      if (updateData.notes) body.notes = updateData.notes;
      await api.put(`/api/partner/jobs/${selectedJob.id}`, body);
      toast.success('Job updated!');
      setSelectedJob(null);
      const { data } = await api.get('/api/partner/jobs');
      setJobs(data);
    } catch {
      toast.error('Failed to update job');
    }
  };

  const loadChat = async () => {
    try {
      const { data } = await api.get('/api/partner/messages');
      setChatMessages(data);
    } catch { /* empty */ }
  };

  const openChat = async () => {
    setShowChat(true);
    await loadChat();
  };

  const sendMessage = async () => {
    if (!chatInput.trim()) return;
    try {
      await api.post('/api/partner/messages', { message: chatInput });
      setChatInput('');
      await loadChat();
    } catch {
      toast.error('Failed to send message');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center py-20 px-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-3xl p-12 border border-gray-200 max-w-lg w-full text-center"
        >
          <div className="w-20 h-20 mx-auto bg-yellow-50 rounded-full flex items-center justify-center mb-6">
            <AlertCircle className="h-10 w-10 text-yellow-500" />
          </div>
          <h2 className="text-2xl font-heading font-bold text-navy" data-testid="partner-portal-error">Access Restricted</h2>
          <p className="mt-3 text-gray-600 leading-relaxed">{error}</p>
          <Button onClick={() => navigate('/partners')} className="mt-6 bg-mint hover:bg-mint-dark text-black rounded-full">
            Apply to be a Partner
          </Button>
        </motion.div>
      </div>
    );
  }

  const activeJobs = jobs.filter(j => j.status !== 'completed' && j.status !== 'cancelled');
  const completedJobs = jobs.filter(j => j.status === 'completed');

  return (
    <div className="min-h-screen bg-gray-50 py-8" data-testid="partner-portal">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-heading font-bold text-navy">Partner Portal</h1>
            <p className="text-gray-500 mt-1">{profile?.company_name} — {profile?.city}</p>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={openChat} data-testid="partner-chat-btn">
              <MessageSquare className="h-4 w-4 mr-2" /> Message Admin
            </Button>
            <Badge className="bg-green-100 text-green-800 text-sm px-3 py-1">Approved Partner</Badge>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
          {[
            { label: 'Active Jobs', value: activeJobs.length, icon: Briefcase, color: 'text-blue-600 bg-blue-50' },
            { label: 'Completed', value: completedJobs.length, icon: CheckCircle, color: 'text-green-600 bg-green-50' },
            { label: 'Total Jobs', value: jobs.length, icon: FileText, color: 'text-purple-600 bg-purple-50' },
            { label: 'Services', value: profile?.services?.length || 0, icon: Building2, color: 'text-mint bg-mint/10' },
          ].map((card, i) => (
            <motion.div
              key={card.label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
              className="bg-white rounded-2xl p-4 border border-gray-200"
            >
              <div className={`w-10 h-10 rounded-xl ${card.color} flex items-center justify-center mb-3`}>
                <card.icon className="h-5 w-5" />
              </div>
              <p className="text-2xl font-heading font-bold text-navy">{card.value}</p>
              <p className="text-xs text-gray-500">{card.label}</p>
            </motion.div>
          ))}
        </div>

        {/* Active Jobs */}
        <div className="mb-8">
          <h2 className="text-xl font-heading font-bold text-navy mb-4">Active Jobs</h2>
          {activeJobs.length === 0 ? (
            <div className="bg-white rounded-2xl p-8 border border-gray-200 text-center text-gray-500">
              <Clock className="h-10 w-10 mx-auto mb-3 text-gray-300" />
              No active jobs at the moment. New jobs will appear here when assigned by the Printed LB team.
            </div>
          ) : (
            <div className="space-y-3">
              {activeJobs.map(job => (
                <div key={job.id} className="bg-white rounded-xl p-4 border border-gray-200">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 flex-wrap">
                        <span className="font-medium text-navy">{job.title}</span>
                        <Badge className={jobStatusColors[job.status]}>{job.status.replace('_', ' ')}</Badge>
                        {job.deadline && <Badge variant="outline">Due: {job.deadline}</Badge>}
                      </div>
                      <p className="text-sm text-gray-600 mt-1">{job.description}</p>
                      {job.order_id && <p className="text-xs text-gray-400 mt-1">Order: #{job.order_id.slice(0,8)}</p>}
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => { setSelectedJob(job); setUpdateData({ status: job.status, notes: job.notes || '' }); }}
                      data-testid={`partner-job-update-${job.id.slice(0,8)}`}
                    >
                      Update Status
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Completed Jobs */}
        {completedJobs.length > 0 && (
          <div>
            <h2 className="text-xl font-heading font-bold text-navy mb-4">Completed Jobs</h2>
            <div className="space-y-3">
              {completedJobs.map(job => (
                <div key={job.id} className="bg-white rounded-xl p-4 border border-gray-200 opacity-70">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                    <div>
                      <span className="font-medium text-navy">{job.title}</span>
                      <p className="text-xs text-gray-400">{new Date(job.created_at).toLocaleDateString()}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Update Job Dialog */}
        <Dialog open={!!selectedJob} onOpenChange={() => setSelectedJob(null)}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading">Update Job: {selectedJob?.title}</DialogTitle>
              <DialogDescription>Update the job status and add notes</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <label className="text-sm font-medium text-navy block mb-2">Status</label>
                <Select value={updateData.status} onValueChange={(v) => setUpdateData({...updateData, status: v})}>
                  <SelectTrigger data-testid="partner-job-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {jobStatuses.map(s => <SelectItem key={s} value={s}>{s.replace('_', ' ')}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-navy block mb-2">Notes</label>
                <Textarea
                  value={updateData.notes}
                  onChange={(e) => setUpdateData({...updateData, notes: e.target.value})}
                  rows={3}
                  placeholder="Production notes..."
                  data-testid="partner-job-notes"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setSelectedJob(null)}>Cancel</Button>
              <Button className="bg-mint hover:bg-mint-dark text-black" onClick={handleUpdateJob} data-testid="partner-job-save-btn">
                Save Update
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Chat with Admin Dialog */}
        <Dialog open={showChat} onOpenChange={setShowChat}>
          <DialogContent className="sm:max-w-lg max-h-[80vh] flex flex-col">
            <DialogHeader>
              <DialogTitle className="font-heading">Message Printed LB Team</DialogTitle>
              <DialogDescription>Ask questions about your jobs or production</DialogDescription>
            </DialogHeader>
            <div className="flex-1 overflow-y-auto space-y-3 py-4 min-h-[200px] max-h-[400px]" data-testid="partner-chat-messages">
              {chatMessages.length === 0 ? (
                <p className="text-center text-gray-400 text-sm py-8">No messages yet. Start the conversation!</p>
              ) : chatMessages.map(msg => (
                <div key={msg.id} className={`flex ${msg.sender === 'partner' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 text-sm ${
                    msg.sender === 'partner'
                      ? 'bg-mint/10 text-navy'
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    <p className="text-xs font-medium mb-1 opacity-60">{msg.sender === 'partner' ? 'You' : 'Printed LB'}</p>
                    <p>{msg.message}</p>
                    <p className="text-[10px] opacity-40 mt-1">{new Date(msg.created_at).toLocaleString()}</p>
                  </div>
                </div>
              ))}
              <div ref={chatEndRef} />
            </div>
            <div className="flex gap-2 pt-2 border-t border-gray-100">
              <Input
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                placeholder="Type a message..."
                onKeyDown={(e) => e.key === 'Enter' && sendMessage()}
                data-testid="partner-chat-input"
              />
              <Button className="bg-mint hover:bg-mint-dark text-black" onClick={sendMessage} data-testid="partner-chat-send">
                <Send className="h-4 w-4" />
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
